import { Fragment, type ReactNode } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { Text } from "@volca/ui/components/ui/text";
import { dayjs } from "@/core/utils/date";
import { INLINE_CONTROL } from "@/profitability/constants";
import { RunGoalStatus } from "@/profitability/goal-checks";
import { MetaList } from "@/profitability/meta-list";
import { CompareSelect } from "@/profitability/overview/compare-select";
import type { ReportOverview } from "@/profitability/overview/types";
import { SettingsUsed } from "@/profitability/settings";
import type { TemplateSetting } from "@/profitability/settings/types";
import type { ProfitabilityRun } from "@/profitability/types";
import { formatReportPeriod } from "@/profitability/utils";
import { periodResultId, RunSelect } from "./run-select";

/**
 * The one quiet line under the period's title: what it is compared with
 * (and the way to change that), when it ran (and the way to open an earlier
 * run of it), a run building, how it matched
 * the owner's own numbers and the settings it read.
 * A figure defined another way since an earlier version gets a line of its
 * own under it, in warning.
 */
export const PeriodContext = ({
  overview,
  run,
  settings,
  compare,
  onCompareChange,
  onRunChange,
}: {
  overview: ReportOverview;
  run: ProfitabilityRun;
  /** The settings the period's run read, from its output. */
  settings: TemplateSetting[] | undefined;
  compare: string | null;
  onCompareChange: (compare: string | null) => void;
  /** A run of the period picked; its newest is the period itself. */
  onRunChange: (runId: string) => void;
}) => {
  const {
    previous,
    running,
    periods = [],
    compare_options: options,
    runs = [],
  } = overview;
  const periodRun = periodResultId(runs, run.id);
  const basis =
    previous?.label ??
    (previous &&
      formatReportPeriod({
        start_date: previous.start_date,
        end_date: previous.end_date,
      }));
  const parts: { key: string; node: ReactNode }[] = [
    {
      key: "compare",
      node:
        basis && options ? (
          <span className="inline-flex items-center gap-1">
            vs
            <CompareSelect
              label={basis}
              value={compare ?? "previous"}
              options={options}
              periods={periods}
              current={periodRun}
              onChange={onCompareChange}
            />
          </span>
        ) : basis ? (
          `vs ${basis}`
        ) : (
          "No earlier period to compare with"
        ),
    },
    ...(runs.length > 1
      ? [
          {
            key: "ran",
            node: (
              <RunSelect runs={runs} current={run.id} onChange={onRunChange} />
            ),
          },
        ]
      : run.completed_at
        ? [
            {
              key: "ran",
              node: `Ran ${dayjs(run.completed_at).format("MMM D, h:mm a")}`,
            },
          ]
        : []),
    ...(running ? [{ key: "running", node: "A newer run is building" }] : []),
    ...(run.goal_checks
      ? [
          {
            key: "goal",
            node: <RunGoalStatus checks={run.goal_checks} inline />,
          },
        ]
      : []),
    ...(settings?.length
      ? [{ key: "settings", node: <SettingsUsedLink settings={settings} /> }]
      : []),
  ];
  return (
    <>
      <Text variant="caption" tone="muted">
        <MetaList className="gap-y-1">
          {parts.map((part) => (
            <Fragment key={part.key}>{part.node}</Fragment>
          ))}
        </MetaList>
      </Text>
      {/* Also when comparable: a figure defined another way since an
          earlier version is left without a change, and this says which. */}
      {previous?.reason && (
        <Text variant="caption" className="text-warning">
          {previous.reason}
        </Text>
      )}
    </>
  );
};

/** The template settings the run read, opened from the line. */
const SettingsUsedLink = ({ settings }: { settings: TemplateSetting[] }) => (
  <SettingsUsed
    settings={settings}
    trigger={
      <Button
        size="sm"
        variant="link"
        color="neutral"
        className={INLINE_CONTROL}
      >
        Settings used
      </Button>
    }
  />
);
